import { useNavigate } from 'react-router-dom';
import {
  CalendarDays, BarChart3, BookOpen, Clock, TrendingUp, TrendingDown, Minus,
  XCircle, AlertCircle, Megaphone, ClipboardList, Calendar, GraduationCap,
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { usePerfilAcademico } from '../../api/hooks/useObservacoes';
import { useAvaliacoes } from '../../api/hooks/useAvaliacoes';
import { TimelineAluno } from '../../components/pedagogico/TimelineAluno';
import { useProfileBrandColor } from '../../theme/useProfileBrandColor';
import '../../components/ui/datatable.css';

/**
 * Dashboard do Aluno — home simples sempre utilizável + KPIs quando houver dados.
 */
export function AlunoDashboard() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { brandColor, brandGradient } = useProfileBrandColor('ALUNO');

  const alunoId = (user as { alunoId?: string } | null)?.alunoId || '';
  const { data: perfil, isLoading: loadingPerfil } = usePerfilAcademico(alunoId || undefined);

  const { useNotasAluno } = useAvaliacoes();
  const { data: notas = [] } = useNotasAluno(alunoId, { anoLetivo: new Date().getFullYear() });
  const notasRecentes = notas.slice(0, 5);

  const primeiroNome = user?.nome?.split(' ')[0] || 'Aluno';
  const escolaNome = user?.nomeEscola || 'Sua escola';

  const getClassificacaoConfig = (c?: string) => {
    const map: Record<string, { label: string; color: string; bg: string; icon: any }> = {
      BEM:         { label: 'Excelente', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', icon: <TrendingUp size={18} /> },
      ATENCAO:     { label: 'Atenção', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', icon: <AlertCircle size={18} /> },
      PREOCUPANTE: { label: 'Preocupante', color: '#f97316', bg: 'rgba(249,115,22,0.12)', icon: <TrendingDown size={18} /> },
      CRITICO:     { label: 'Crítico', color: '#ef4444', bg: 'rgba(239,68,68,0.12)', icon: <XCircle size={18} /> },
    };
    return map[c || 'BEM'] || map.BEM;
  };

  const cls = getClassificacaoConfig(perfil?.risco?.classificacao);

  const atalhos = [
    { icon: <CalendarDays size={18} />, label: 'Minha Grade', to: '/aluno/grade', accent: '#2563eb' },
    { icon: <BarChart3 size={18} />, label: 'Frequência', to: '/aluno/frequencia', accent: '#22c55e' },
    { icon: <Megaphone size={18} />, label: 'Comunicados', to: '/aluno/comunicados', accent: '#7c3aed' },
    { icon: <Calendar size={18} />, label: 'Agenda', to: '/aluno/agenda', accent: '#0284c7' },
    { icon: <GraduationCap size={18} />, label: 'Video Aulas', to: '/aluno/ead', accent: brandColor },
  ];

  return (
    <div className="admin-page">
      {/* Hero */}
      <div
        data-tour="dash.aluno.hero"
        style={{
          background: brandGradient || `linear-gradient(135deg, ${brandColor}, #3d1a20)`,
          borderRadius: 16,
          padding: '22px 24px',
          marginBottom: 18,
          color: '#fff',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ fontSize: '.68rem', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', opacity: 0.75, marginBottom: 6 }}>
            Espaço do aluno
          </div>
          <h1 style={{ margin: 0, fontSize: '1.45rem', fontWeight: 800 }}>Olá, {primeiroNome}!</h1>
          <p style={{ margin: '6px 0 0', fontSize: '.85rem', opacity: 0.85 }}>
            {escolaNome} — acompanhe grade, frequência e comunicados.
          </p>
        </div>
        <div
          style={{
            position: 'absolute',
            right: -20,
            top: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,.08)',
          }}
        />
      </div>

      {/* Atalhos — sempre visíveis */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 18 }}>
        {atalhos.map((a) => (
          <button
            key={a.to}
            type="button"
            onClick={() => navigate(a.to)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 8,
              padding: '14px 14px',
              borderRadius: 12,
              background: 'var(--bg-card, #fff)',
              border: '1px solid var(--border-color, #e5e7eb)',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <span style={{ color: a.accent }}>{a.icon}</span>
            <span style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-main)' }}>{a.label}</span>
          </button>
        ))}
      </div>

      {/* KPIs acadêmicos (quando houver vínculo alunoId + API) */}
      {alunoId && loadingPerfil && (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: '.85rem' }}>
          Carregando dados acadêmicos...
        </div>
      )}

      {alunoId && !loadingPerfil && perfil && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div className="kpi-card hover-lift" style={{ gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CalendarDays size={16} style={{ color: '#3b82f6' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)' }}>Frequência</span>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: perfil.frequencia.percentual >= 75 ? '#22c55e' : '#ef4444' }}>
                {perfil.frequencia.percentual.toFixed(1)}%
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                {perfil.frequencia.presentes}/{perfil.frequencia.totalDias} dias
              </div>
            </div>

            <div className="kpi-card hover-lift" style={{ gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <BarChart3 size={16} style={{ color: '#7c3aed' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)' }}>Média Geral</span>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: perfil.notas.mediaGeral >= 7 ? '#22c55e' : perfil.notas.mediaGeral >= 5 ? '#f59e0b' : '#ef4444' }}>
                {perfil.notas.mediaGeral.toFixed(1)}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                {perfil.notas.tendencia === 'SUBINDO' ? <TrendingUp size={12} style={{ color: '#22c55e' }} /> :
                 perfil.notas.tendencia === 'DESCENDO' ? <TrendingDown size={12} style={{ color: '#ef4444' }} /> :
                 <Minus size={12} />}
                <span>{perfil.notas.tendencia === 'SUBINDO' ? 'Subindo' : perfil.notas.tendencia === 'DESCENDO' ? 'Descendo' : 'Estável'}</span>
              </div>
            </div>

            <div className="kpi-card hover-lift" style={{ gap: 8, background: cls.bg }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {cls.icon}
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: cls.color }}>Status</span>
              </div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: cls.color }}>{cls.label}</div>
              <div style={{ fontSize: '0.68rem', color: cls.color, opacity: 0.7 }}>Score: {perfil.risco.score}</div>
            </div>

            <div className="kpi-card hover-lift" style={{ gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ClipboardList size={16} style={{ color: '#dc2626' }} />
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)' }}>Avaliações</span>
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-main)' }}>{notas.length}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>notas lançadas</div>
            </div>
          </div>

          {perfil.notas.materias.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <BookOpen size={16} style={{ color: '#7c3aed' }} />
                Médias por Matéria
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                {perfil.notas.materias.map((m) => (
                  <div key={m.materiaId} style={{
                    padding: '12px 14px', borderRadius: 10,
                    background: 'var(--bg-main, #f9fafb)', border: '1px solid var(--border-color, #e5e7eb)',
                  }}>
                    <div style={{ fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
                      {m.materiaNome}
                    </div>
                    <div style={{
                      fontSize: '1.2rem', fontWeight: 800,
                      color: m.media >= 7 ? '#22c55e' : m.media >= 5 ? '#f59e0b' : '#ef4444',
                    }}>
                      {m.media.toFixed(1)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {notasRecentes.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <ClipboardList size={16} style={{ color: '#dc2626' }} />
                Últimas Notas
              </div>
              {notasRecentes.map((n: any) => (
                <div key={n.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                  borderBottom: '1px solid var(--border-color, #f0f0f0)',
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-main)' }}>
                      {n.avaliacao?.titulo}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      {n.avaliacao?.materia?.nome} • {n.avaliacao?.tipo}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {n.valorObtido != null ? (
                      <span style={{
                        fontSize: '1rem', fontWeight: 800,
                        color: (n.valorObtido / (n.avaliacao?.valorMaximo || 10)) >= 0.6 ? '#22c55e' : '#ef4444',
                      }}>
                        {n.valorObtido}/{n.avaliacao?.valorMaximo}
                      </span>
                    ) : n.faltou ? (
                      <span style={{ color: '#ef4444', fontWeight: 600, fontSize: '0.78rem' }}>Faltou</span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>—</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {alunoId && !loadingPerfil && !perfil && (
        <div style={{ ...cardStyle, marginBottom: 16, textAlign: 'center', padding: 28 }}>
          <BookOpen size={40} style={{ color: brandColor, margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: '.88rem', margin: 0 }}>
            Seus indicadores acadêmicos aparecem aqui quando a escola lançar notas e frequência.
          </p>
        </div>
      )}

      {!alunoId && (
        <div style={{ ...cardStyle, marginBottom: 16, textAlign: 'center', padding: 28 }}>
          <BookOpen size={40} style={{ color: brandColor, margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--text-muted)', fontSize: '.88rem', margin: 0 }}>
            Bem-vindo! Use os atalhos acima para navegar no seu painel.
          </p>
        </div>
      )}

      {alunoId && (
        <div style={cardStyle}>
          <div style={{ fontWeight: 700, fontSize: '0.92rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={16} style={{ color: '#6366f1' }} />
            Minha Timeline
          </div>
          <TimelineAluno alunoId={alunoId} limit={15} />
        </div>
      )}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  borderRadius: 14, background: 'var(--bg-card, #fff)', padding: 16,
  border: '1px solid var(--border-color, #e5e7eb)', marginBottom: 16,
};
